import { ObservableValue } from './ObservableValue'
import { CutterAudio } from './entities/CutterAudio'
import { Snapshot } from './Snapshot'
import { cropAudio, cutAudio } from './functions/cutting'
import { PositionRange } from '../types'
import { fixVisibleRange } from './functions/fixVisibleRange'

type SnapshotState = {
  [key in keyof Snapshot]: ObservableValue<Snapshot[key]>
}

export class CutterState implements SnapshotState {
  public readonly visibleRange: ObservableValue<PositionRange>
  public readonly selection = new ObservableValue<PositionRange | null>(null)
  public readonly prevSnapshots = new ObservableValue<Snapshot[]>([])
  public readonly nextSnapshots = new ObservableValue<Snapshot[]>([])
  public readonly audio: ObservableValue<CutterAudio>

  constructor(audioBuffer: AudioBuffer) {
    this.audio = new ObservableValue(new CutterAudio(audioBuffer))
    this.visibleRange = new ObservableValue([0, audioBuffer.length])
  }

  public crop = () => {
    if (!this.selection.value) {
      return
    }
    const selection = this.selection.value
    this.commit()
    this.selection.value = null
    this.audio.value = cropAudio(this.audio.value, selection)
    this.visibleRange.value = [0, this.audio.value.length]
  }

  public remove = () => {
    if (!this.selection.value) {
      return
    }
    const selection = this.selection.value
    this.commit()
    this.selection.value = null
    this.audio.value = cutAudio(this.audio.value, selection)
    this.visibleRange.value = fixVisibleRange(this.visibleRange.value, selection, this.audio.value.length)
  }

  public undo = () => {
    if (!this.prevSnapshots.value.length) {
      return
    }
    const prevSnapshots = [...this.prevSnapshots.value]
    const prevSnapshot = prevSnapshots.pop()!
    this.prevSnapshots.value = prevSnapshots
    this.nextSnapshots.value = [...this.nextSnapshots.value, this.getSnapshot()]
    this.applySnapshot(prevSnapshot)
  }

  public redo = () => {
    if (!this.nextSnapshots.value.length) {
      return
    }
    const nextSnapshots = [...this.nextSnapshots.value]
    const nextSnapshot = nextSnapshots.pop()!
    this.nextSnapshots.value = nextSnapshots
    this.prevSnapshots.value = [...this.prevSnapshots.value, this.getSnapshot()]
    this.applySnapshot(nextSnapshot)
  }

  public commit() {
    this.prevSnapshots.value = [...this.prevSnapshots.value, this.getSnapshot()]
    this.nextSnapshots.value = []
  }

  private getSnapshot(): Snapshot {
    return {
      visibleRange: this.visibleRange.value,
      selection: this.selection.value,
      audio: this.audio.value,
    }
  }

  private applySnapshot(snapshot: Snapshot) {
    const keys = Object.keys(snapshot) as Array<keyof Snapshot>
    keys.forEach((key) => {
      this[key].value = snapshot[key]
    })
  }
}
